import { TabPanel, TabView } from "primereact/tabview";
import SpendAnalysis from "./SpendAnalysis/SpendAnalysis";
import TenderAnalysis from "./TenderAnalysis/TenderAnalysis";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import WidgetBoard from "./WidgetBoard";
import { useNavigationContextStore } from "../../stores/navigationContextStore";
import { useCustomDashboardStore } from "../../stores/customDashboardStore";
import { useFeatureFlagsStore, FeatureFlag } from "../../stores/featureFlagsStore";
import CheckIconCustom from "../../assets/images/CheckIconCustom";

const Analysis = () => {
	const { negotiation_id } = useParams<{ negotiation_id?: string }>();
	const negotiationId = useMemo(
		() => (negotiation_id ? parseInt(negotiation_id, 10) : null),
		[negotiation_id]
	);
	const { customTabs, loadCustomTabs, createCustomTab, deleteCustomTab, loadingCustomTabs } = useCustomDashboardStore();
	const { checkFeatureFlag } = useFeatureFlagsStore();
	const [activeIndex, setActiveIndex] = useState(0);
	const [newTabName, setNewTabName] = useState("");
	const [showTabInput, setShowTabInput] = useState(false);
	const [customTabsEnabled, setCustomTabsEnabled] = useState(false);
	const { setAnalysisPageName } = useNavigationContextStore();
	const tabViewRef = useRef<HTMLDivElement>(null);
	const [buttonLeft, setButtonLeft] = useState(0);

	useEffect(() => {
		if (negotiationId) {
			loadCustomTabs(negotiationId);
			// Check if CUSTOM_TAB feature is enabled
			checkFeatureFlag(negotiationId, FeatureFlag.CUSTOM_TAB).then((enabled) => {
				setCustomTabsEnabled(enabled);
			});
		}
	}, [negotiationId, loadCustomTabs, checkFeatureFlag]);

	const addCustomTab = async () => {
		if (!negotiationId || !newTabName.trim() || !customTabsEnabled) return;
		try {
			const created = await createCustomTab(negotiationId, newTabName.trim());
			setNewTabName("");
			setShowTabInput(false);
			// select new tab (base tabs 3 indexes)
			setActiveIndex(baseTabs.length + customTabs.length);
			if (created?.page_name) {
				setAnalysisPageName(created.page_name);
			}
		} catch (e) {
			console.error(e);
		}
	};

	const baseTabs = [
		{ header: "Tender Summary", content: <TenderAnalysis /> },
		{ header: "Spend Analysis", content: <SpendAnalysis /> },
		{
			header: "Cost Insights (Coming Soon)",
			content: (
				<p className="m-0">
					Sed ut perspiciatis unde omnis iste natus error sit voluptatem
					accusantium doloremque laudantium, totam rem aperiam, eaque ipsa
					quae ab illo inventore veritatis et quasi architecto beatae vitae
					dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit
					aspernatur aut odit aut fugit, sed quia consequuntur magni dolores
					eos qui ratione voluptatem sequi nesciunt. Consectetur, adipisci
					velit, sed quia non numquam eius modi.
				</p>
			),
			disabled: true,
		},
	];

	const handleTabChange = (index: number) => {
		setActiveIndex(index);
		if (index >= baseTabs.length) {
			const custom = customTabs[index - baseTabs.length];
			setAnalysisPageName(custom?.page_name || null);
		} else {
			setAnalysisPageName(null);
		}
	};

	// Position the add button to the right of the last tab (similar to ScenarioPlanning)
	useEffect(() => {
		const updateButtonPosition = () => {
			if (!customTabsEnabled || !tabViewRef.current) return;
			const nav = tabViewRef.current.querySelector(".p-tabview-nav");
			if (!nav) return;
			const tabCount = baseTabs.length + customTabs.length;
			const lastTab = nav.children[tabCount - 1] as HTMLElement | undefined;
			if (!lastTab) return;
			const navRect = tabViewRef.current.getBoundingClientRect();
			const lastRect = lastTab.getBoundingClientRect();
			setButtonLeft(lastRect.right - navRect.left + 12);
		};

		updateButtonPosition();
		const timer = setTimeout(updateButtonPosition, 100);
		return () => clearTimeout(timer);
	}, [baseTabs.length, customTabs.length, activeIndex, customTabsEnabled]);

	return (
		<div className="simple-tabs">
			<div style={{ position: "relative" }} ref={tabViewRef}>
			<TabView activeIndex={activeIndex} onTabChange={(e) => handleTabChange(e.index)}>
				{baseTabs.map((tab) => (
					<TabPanel key={tab.header} header={tab.header} disabled={tab.disabled}>
						{tab.content}
					</TabPanel>
				))}
				{customTabsEnabled && customTabs.map((tab) => (
					<TabPanel
						key={tab.id}
						header={
							<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
								<span>{tab.page_name}</span>
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										if (!negotiationId) return;
										const confirmed = window.confirm(`Delete tab "${tab.page_name}"?`);
										if (!confirmed) return;
										deleteCustomTab(tab.id);
										// move to first tab after delete
										setActiveIndex(0);
										setAnalysisPageName(null);
									}}
									style={{
										border: "none",
										background: "transparent",
										cursor: "pointer",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										padding: 0,
									}}
									title="Delete tab"
								>x
								</button>
							</div>
						}
					>
						{negotiationId ? (
							<WidgetBoard negotiationId={negotiationId} pageName={tab.page_name} />
						) : (
							<div className="p-4">No negotiation selected.</div>
						)}
					</TabPanel>
				))}
			</TabView>
			{customTabsEnabled && (
				<div style={{ position: "absolute", top: "10px", left: `${buttonLeft}px`, display: "flex", alignItems: "center", gap: "8px" }}>
					{showTabInput && (
						<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
							<input
								type="text"
								value={newTabName}
								placeholder="Tab name"
								autoFocus
								onChange={(e) => setNewTabName(e.target.value)}
								style={{
									padding: "4px 6px",
									borderRadius: "6px",
									border: "1px solid var(--border-muted)",
									width: "150px",
								}}
							/>
							<button
								type="button"
								onClick={addCustomTab}
								disabled={!newTabName.trim() || !negotiationId}
								style={{
									padding: "4px 6px",
									borderRadius: "6px",
									border: "1px solid var(--color-blue-500)",
									background: "var(--color-blue-500)",
									color: "var(--surface-default)",
									cursor: !newTabName.trim() || !negotiationId ? "not-allowed" : "pointer",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<CheckIconCustom />
							</button>
						</div>
					)}
					<button
						type="button"
						onClick={() => {
							setShowTabInput((prev) => !prev);
							setNewTabName("");
						}}
						title="Add tab"
						style={{
							width: "32px",
							height: "26px",
							minWidth: "32px",
							minHeight: "26px",
							borderRadius: "6px",
							border: "1px solid var(--color-blue-500)",
							background: "var(--color-blue-500)",
							color: "var(--surface-default)",
							cursor: "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						{showTabInput ? "x" : "+"}
					</button>
				</div>
			)}
			</div>
			{loadingCustomTabs && <span style={{ color: "var(--text-muted)" }}>Loading tabs…</span>}
		</div>
	);
};

export default Analysis;
